/* The walkthrough's machinery: a veil, a spotlight, and a card beside it.
 *
 * THE CARD RENDERS FIRST AND THE SPOTLIGHT CATCHES UP. Screens fetch their data, so a
 * route change is not finished when the hash changes. The person always has something
 * to read while the screen loads, and the OLD spotlight is put out immediately: a ring
 * lit over the wrong thing is worse than no ring at all.
 *
 * A STEP THAT CANNOT FIND ITS TARGET STILL SHOWS. It centres the card and says its
 * piece. The alternative is a walkthrough that breaks silently when a panel is
 * renamed, on the one screen a prospect is being shown.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { TOURS, type TourStep } from './scripts.js';
import { go } from '../routes.js';

interface Box { left: number; top: number; width: number; height: number }

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Poll until the thing exists, or give up and let the step show without it. */
async function waitFor<T>(find: () => T | null, ms = 4000): Promise<T | null> {
  const until = Date.now() + ms;
  while (Date.now() < until) {
    const v = find();
    if (v) return v;
    await wait(80);
  }
  return null;
}

export function Tour({ seat, onEnd }: { seat: string; onEnd: () => void }) {
  const steps: TourStep[] = TOURS[seat] ?? [];
  const [i, setI] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const target = useRef<Element | null>(null);
  const host = useRef<HTMLDivElement>(null);
  /* Every show() is numbered. A person pressing Next while the previous step is still
     waiting for its screen must not have the old step's spotlight land on top. */
  const seq = useRef(0);

  const place = useCallback(() => {
    const t = target.current;
    if (!t || !t.isConnected) { setBox(null); return; }
    const r = t.getBoundingClientRect();
    if (!r.width && !r.height) { setBox(null); return; }
    setBox({ left: r.left - 8, top: r.top - 8, width: r.width + 16, height: r.height + 16 });
  }, []);

  /* Re-shown on every step, not once: a prep step can open a dialog, and the top
     layer stacks in the order things entered it. */
  const lift = useCallback(() => {
    const el = host.current;
    if (!el) return;
    try { el.hidePopover(); } catch { /* not open */ }
    try { el.showPopover(); } catch { /* older engines: the fixed fallback */ }
  }, []);

  useEffect(() => {
    const step = steps[i];
    if (!step) return;
    const mine = ++seq.current;
    lift();
    target.current = null;
    setBox(null);

    void (async () => {
      if (step.route && window.location.hash !== step.route) {
        /* Wait for the NAMED screen, not merely for "some page". A step can otherwise
           match a panel on the screen being left - two consoles both have a panel
           called Going quiet - and the spotlight lands on a node that is about to
           vanish. `data-screen` is set by the shell and is the only reliable signal
           that the new screen is the one rendering. */
        const want = step.route.replace(/^#\//, '');
        go(step.route);
        await waitFor(() => document.querySelector(`.page[data-screen="${want}"]`));
      }
      if (seq.current !== mine) return;
      if (step.prep) {
        try { await step.prep(); } catch { /* the step still shows */ }
        lift();   // the prep may have opened a dialog
      }
      if (seq.current !== mine) return;
      if (!step.target) return;
      const found = await waitFor(() => step.target?.() ?? null);
      if (seq.current !== mine || !found) return;
      target.current = found;
      found.scrollIntoView({ block: 'center', inline: 'nearest' });
      await wait(240);
      if (seq.current !== mine) return;
      place();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, seat, lift]);

  useEffect(() => {
    const on = () => place();
    window.addEventListener('resize', on);
    document.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      document.removeEventListener('scroll', on, true);
    };
  }, [place]);

  /* The end-of-tour check is OUTSIDE the updater. A state updater is a pure function
     React may call during render, and onEnd() sets state on the shell: calling it in
     there is a setState during another component's render, which React warns about
     and which would eventually bite as a dropped update. */
  const next = useCallback(() => {
    if (i >= steps.length - 1) onEnd();
    else setI(i + 1);
  }, [i, steps.length, onEnd]);
  const back = useCallback(() => setI((n) => Math.max(0, n - 1)), []);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onEnd(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [next, back, onEnd]);

  const step = steps[i];
  if (!step) return null;
  const first = i === 0;
  const last = i === steps.length - 1;
  const card = cardPosition(box);

  return (
    /* IN THE TOP LAYER, as a popover, for the same reason the toasts are: a step can
       point at something inside an open dialog - the register is a dialog - and
       nothing outside the top layer can be painted over a modal's backdrop. */
    <div className="tour" ref={host} popover="manual">
      <div className={'tour-veil' + (box ? '' : ' dim')} onClick={() => onEnd()} />
      {box && (
        <div className="tour-spot" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} />
      )}
      <div className={'tour-card' + (box ? '' : ' center')} style={card}>
        <div className="eyebrow t-label-xsmall">
          {first ? 'Walkthrough'
            : last ? 'Walkthrough · done'
            : `Walkthrough · ${i} of ${steps.length - 2}`}
        </div>
        <h3 className="t-heading-h3">{step.title}</h3>
        <p className="t-body-small">{step.body}</p>
        <div className="foot">
          {!last && (
            <button type="button" className="linkish t-label-small" onClick={onEnd}>
              {first ? 'Skip, I will explore on my own' : 'Skip walkthrough'}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {!first && !last && (
            <button type="button" className="btn t-label-small" onClick={back}>Back</button>
          )}
          <button type="button" className="btn primary t-label-small" onClick={next}>
            {first ? 'Start the walkthrough' : last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Below the target if there is room, else above, else beside it. */
function cardPosition(box: Box | null): React.CSSProperties {
  if (!box) return {};
  const gap = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(380, vw - 32);
  const h = 260;
  let left = Math.min(Math.max(16, box.left), vw - w - 16);
  let top: number;
  if (box.top + box.height + gap + h + 16 <= vh) top = box.top + box.height + gap;
  else if (box.top - gap - h - 16 >= 0) top = box.top - gap - h;
  else {
    top = Math.max(16, Math.min(vh - h - 16, box.top));
    left = box.left + box.width + gap + w <= vw
      ? box.left + box.width + gap
      : Math.max(16, box.left - gap - w);
  }
  return { left, top, width: w };
}
